// Análisis de paros (coordinador) — página unificada con tabs.
//
// Fusiona las antiguas C6 (ranking de pérdida por línea) y C8 (distribución de
// paros por causal) en UNA sola pantalla con:
//   - RoleLayout + CoordinadorSidebar + header únicos
//   - Período COMPARTIDO (lib/periodo, persistido en la URL: ?p=&ancla= o
//     ?p=rango&desde=&hasta=) — el mismo que usa el Dashboard, así el drill-down
//     llega con el contexto — más el filtro de área. Persisten entre tabs.
//   - Tab en URL (?tab=lineas|causal|pits, default lineas). Cada tab responde
//     una pregunta distinta y usa el NOMBRE de su métrica (diccionario v1):
//     "Pérdida por línea" (B1: paros cerrados cuya categoría afecta
//     disponibilidad), "Paro total registrado" (B2: todos los cerrados, incluidos
//     programados) y "Entrada a Pits" (B5: duración de cambio vs meta).
//     Nunca el mismo "paro" con bases distintas sin decirlo.
//   - DN1: los paros abiertos se muestran aparte ("N en curso"), no suman.
//   - Deep-link compat: ?linea_id=X enfoca la fila en el tab "Por línea".
//
// El contenedor SIEMPRE hace useCoordinadorAnalisisParos({...base, linea_id}):
// su `recursos` (que incluye `lineas`, exclusivo de este endpoint) alimenta la
// barra base + el dropdown de Línea del tab causal; su `data` alimenta el tab
// "Por causal". El tab "Por línea" hace su propio useCoordinadorAnalisisLineas.

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import type {
  AnalisisLinea,
  AnalisisLineasQuery,
  AnalisisParosQuery,
  CoordinadorAnalisisParos,
  CoordinadorEntradaPits,
  EntradaPitsQuery,
  ParoPorHora,
  ParoPorTurno,
  PitsPorPersona,
  ProduccionPorHora,
  ProduccionPorTurno,
  TendenciaParo,
} from 'schemas';
import { BarraPeriodo, usePeriodo, type Preset } from '../../lib/periodo/periodo';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  LabelList,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  EtiquetaValor,
  etiquetaDonutPct,
  pasoEtiquetas,
} from '../../components/charts/value-labels';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Icon } from '../../components/ui/icon';
import { KpiCard as KpiCardShared } from '../../components/ui/kpi-card';
import { Label } from '../../components/ui/label';
import { SegmentedControl } from '../../components/ui/segmented-control';
import { Select } from '../../components/ui/select';
import { Sparkline } from '../../components/ui/sparkline';
import { Spinner } from '../../components/ui/spinner';
import { TBody, TD, TH, THead, TR, Table } from '../../components/ui/table';
import { CoordinadorSidebar } from '../../components/layout/CoordinadorSidebar';
import { RoleLayout } from '../../components/layout/RoleLayout';
import {
  useCoordinadorAnalisisLineaParos,
  useCoordinadorAnalisisLineas,
  useCoordinadorAnalisisParos,
  useCoordinadorEntradaPits,
} from '../../lib/api/queries';
import { useAuth } from '../../lib/auth';
import { cn } from '../../lib/utils';
import { semaforoCumplimiento, toneTextClass } from '../../lib/semaforo';
import { supabase, API_URL } from '../../lib/supabase';

// =============================================================================
// Tipos del contenedor
// =============================================================================

/** Filtros base compartidos por los tabs: período (del selector) + área. */
type BaseFiltros = {
  desde?: string;
  hasta?: string;
  area_id?: string;
};

type TabId = 'lineas' | 'causal' | 'pits';

/** Presets del selector compartido en esta pantalla (política 2). */
const PRESETS_PAROS: Preset[] = ['hoy', 'semana', 'mes', 'rango'];

/** Pregunta que responde cada tab (diccionario v1) — se muestra bajo el control. */
const PREGUNTA_TAB: Record<TabId, { titulo: string; pregunta: string; base: string }> = {
  lineas: {
    titulo: 'Pérdida por línea',
    pregunta: '¿Cuánto tiempo de producción se perdió?',
    base: 'Paros cuya categoría afecta disponibilidad, solo eventos cerrados; los abiertos se muestran aparte (DN1). La pérdida en $ usa el precio vigente por SKU.',
  },
  causal: {
    titulo: 'Paro total registrado',
    pregunta: '¿En qué se usó el tiempo? · incluye programados',
    base: 'TODOS los paros cerrados, incluidos los programados (por eso da otro número que "Pérdida por línea"). La causal madre es la del momento de captura (DN2).',
  },
  pits: {
    titulo: 'Entrada a Pits',
    pregunta: '¿Cuánto dura un cambio vs su meta?',
    base: 'Solo las causales configuradas para el indicador, eventos cerrados; promedio de minutos por evento vs la meta (con override por línea si existe).',
  },
};

/** Texto del comparativo (DN8: a la misma altura, misma cobertura). */
function etiquetaComparativo(per: ReturnType<typeof usePeriodo>): string {
  if (!per.periodo.isCurrent) return 'vs período anterior';
  if (per.preset === 'hoy') return 'vs ayer';
  if (per.preset === 'semana') return 'vs mismo corte de la sem. anterior';
  if (per.preset === 'mes') return 'vs mismo corte del mes anterior';
  return 'vs período anterior equivalente';
}

// =============================================================================
// Helpers compartidos
// =============================================================================

function fmtNumero(n: number): string {
  return Math.round(n).toLocaleString('es-CO');
}

/** Pesos con separador de miles es-CO. Negativos con signo menos antes del $. */
function fmtCop(n: number): string {
  const r = Math.round(n);
  return `${r < 0 ? '−' : ''}$${Math.abs(r).toLocaleString('es-CO')}`;
}

function fmtHoras(horas: number): string {
  if (horas <= 0) return '0h';
  const h = Math.floor(horas);
  const m = Math.round((horas - h) * 60);
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

function fmtPct(n: number | null, decimals = 1): string {
  if (n === null) return '—';
  return `${n.toFixed(decimals)}%`;
}

function fmtFechaCorta(iso: string): string {
  if (!iso || iso.length < 10) return iso;
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Intl.DateTimeFormat('es-CO', {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  })
    .format(new Date(Date.UTC(y, m - 1, d)))
    .replace(/\.|,/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Delta con signo explícito en ambos sentidos ("+2h 10m" / "−6h 46m"); "" si es 0. */
function fmtDeltaHoras(delta: number): string {
  if (delta === 0) return '';
  const sign = delta > 0 ? '+' : '−';
  return `${sign}${fmtHoras(Math.abs(delta))}`;
}

/** Tiempo a partir de minutos: "< 60 min" como "N min", luego usa fmtHoras. */
function fmtTiempoMin(minutos: number): string {
  if (minutos <= 0) return '0 min';
  if (minutos < 60) return `${Math.round(minutos)} min`;
  return fmtHoras(minutos / 60);
}

/** "{n} evento" / "{n} eventos". */
// `sufijo` concuerda con el número junto al sustantivo. Sin él salía
// "1 evento cerrados", que se leía en una captura del material comercial.
function fmtEventos(n: number, sufijo?: 'cerrado'): string {
  const base = `${fmtNumero(n)} ${n === 1 ? 'evento' : 'eventos'}`;
  if (!sufijo) return base;
  return `${base} ${n === 1 ? sufijo : `${sufijo}s`}`;
}

// =============================================================================
// ParosBarTooltip — tooltip custom reutilizable para los BarChart de paros.
// Muestra: label · tiempo · eventos. Lee del objeto original (payload[0].payload),
// que incluye paros_count y el valor temporal (horas o minutos).
// =============================================================================

type ParosBarDatum = {
  label: string;
  paros_count: number;
  horas?: number;
  minutos?: number;
  cumplimiento_pct?: number | null;
};

function ParosBarTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ payload?: ParosBarDatum }>;
}) {
  if (!active || !payload || payload.length === 0) return null;
  const d = payload[0]?.payload;
  if (!d) return null;
  const tiempo =
    typeof d.horas === 'number' ? fmtHoras(d.horas) : fmtTiempoMin(Number(d.minutos ?? 0));
  return (
    <div className="rounded-md border border-border bg-surface-elevated px-2.5 py-1.5 text-xs text-fg shadow-sm">
      <p className="font-medium">{d.label}</p>
      <p className="mt-0.5 text-fg-secondary tabular-nums">
        {tiempo} de paro · {fmtEventos(d.paros_count)}
      </p>
      {typeof d.cumplimiento_pct === 'number' && (
        <p className="mt-0.5 text-fg-secondary tabular-nums">
          {d.cumplimiento_pct.toFixed(1)}% cumplimiento vs. meta
        </p>
      )}
    </div>
  );
}

// Estado de la línea = combinación de dos señales:
//   - Cumplimiento → semáforo unificado 90/70 (helper compartido).
//   - Horas de paro → ≥8h fuerza al menos "Atención" (señal operativa propia).
// El corte de cumplimiento viene del helper; la condición de paro queda explícita.
function estadoLinea(l: AnalisisLinea): { tono: 'ok' | 'warn' | 'crit'; label: string } {
  const tonoCumpl = semaforoCumplimiento(l.cumplimiento_pct);
  if (tonoCumpl === 'crit') return { tono: 'crit', label: 'Crítico' };
  if (l.horas_paro >= 8 || tonoCumpl === 'warn') return { tono: 'warn', label: 'Atención' };
  return { tono: 'ok', label: 'OK' };
}

/** Colapsa nombres con palabra repetida en display (ej. "Otro Otro" → "Otro"). */
function nombreNormalizado(nombre: string): string {
  const partes = nombre.trim().split(/\s+/);
  if (partes.length === 2 && partes[0] && partes[0].toLowerCase() === partes[1]?.toLowerCase()) {
    return partes[0];
  }
  return nombre;
}

// =============================================================================
// Contenedor principal
// =============================================================================

export function AnalisisParosPage() {
  const { state } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // El tab explícito (?tab=causal) manda. Sin param → 'lineas' por defecto
  // (compat deep-link C1/C5, que entran con linea_id y sin tab). Antes el path
  // /analisis-lineas forzaba 'lineas' y dejaba el tab "Por causal" inalcanzable.
  const tabParam = searchParams.get('tab');
  const tab: TabId = tabParam === 'causal' ? 'causal' : tabParam === 'pits' ? 'pits' : 'lineas';

  // linea_id de la URL = FOCO/scroll del tab "Por línea" (deep-link C1/C5).
  const focusLineaId = searchParams.get('linea_id');

  // Período COMPARTIDO con el Dashboard (misma URL: ?p=&ancla= / ?p=rango&desde=&hasta=).
  const per = usePeriodo(undefined, 'semana', { url: true });
  // Filtro de área (lifting al contenedor). Persiste entre tabs.
  const [areaId, setAreaId] = useState<string | undefined>(undefined);
  const base = useMemo<BaseFiltros>(
    () => ({ desde: per.periodo.desde, hasta: per.periodo.hasta, area_id: areaId }),
    [per.periodo.desde, per.periodo.hasta, areaId],
  );

  // Filtro Línea PROPIO del tab causal (distinto del foco del tab líneas).
  const [causalLineaId, setCausalLineaId] = useState<string | undefined>(undefined);

  // El contenedor SIEMPRE fetchea analisis-paros: alimenta barra base + tab causal.
  const parosQuery: AnalisisParosQuery = useMemo(
    () => ({
      ...base,
      linea_id: causalLineaId,
    }),
    [base, causalLineaId],
  );
  const { data, isLoading, isError, error } = useCoordinadorAnalisisParos(parosQuery);

  const setTab = (next: TabId) => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', next);
    setSearchParams(params, { replace: true });
  };

  if (state.kind !== 'signed_in') return <Navigate to="/login" replace />;

  if (isLoading) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-3 bg-surface-base p-6 text-fg">
        <Spinner className="size-6" />
        <p className="text-sm text-fg-secondary">Cargando análisis…</p>
      </main>
    );
  }

  if (isError || !data) {
    return (
      <main className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center gap-3 bg-surface-base p-6 text-center text-fg">
        <h1 className="text-lg font-medium text-crit">No pudimos cargar</h1>
        <p className="text-sm text-fg-secondary">
          {error instanceof Error ? error.message : 'Error desconocido'}
        </p>
      </main>
    );
  }

  return (
    <RoleLayout sidebar={<CoordinadorSidebar />} roleLabel="Coordinador">
      <div className="flex flex-col gap-4">
        <header className="border-b border-border pb-3">
          <h1 className="text-xl font-medium text-fg">Análisis de paros</h1>
          <p className="mt-1 text-[11px] text-fg-tertiary">
            Tres preguntas distintas, tres métricas con nombre propio — nunca el mismo
            &quot;paro&quot; con bases distintas.
          </p>
        </header>

        <FiltrosBaseBar
          per={per}
          recursos={data.recursos}
          areaId={areaId}
          onChangeArea={setAreaId}
        />

        <div className="flex flex-wrap items-center justify-between gap-2">
          <SegmentedControl<TabId>
            value={tab}
            onChange={setTab}
            size="sm"
            options={[
              { value: 'lineas', label: PREGUNTA_TAB.lineas.titulo },
              { value: 'causal', label: PREGUNTA_TAB.causal.titulo },
              { value: 'pits', label: PREGUNTA_TAB.pits.titulo },
            ]}
          />
          <p className="text-[11px] text-fg-tertiary">{PREGUNTA_TAB[tab].pregunta}</p>
        </div>

        {tab === 'lineas' ? (
          <RankingLineasTab base={base} per={per} focusLineaId={focusLineaId} />
        ) : tab === 'causal' ? (
          <DistribucionParosTab
            data={data}
            lineaId={causalLineaId}
            onChangeLinea={setCausalLineaId}
          />
        ) : (
          <EntradaPitsTab base={base} />
        )}

        <footer className="rounded-md border border-border bg-surface-overlay/40 px-3 py-2 text-[11px] text-fg-tertiary">
          <span className="font-medium text-fg-secondary">Base de esta pestaña:</span>{' '}
          {PREGUNTA_TAB[tab].base}
        </footer>
      </div>
    </RoleLayout>
  );
}

// =============================================================================
// FiltrosBaseBar — período compartido (selector de lib/periodo) + área
// =============================================================================

function FiltrosBaseBar({
  per,
  recursos,
  areaId,
  onChangeArea,
}: {
  per: ReturnType<typeof usePeriodo>;
  recursos: CoordinadorAnalisisParos['recursos'];
  areaId: string | undefined;
  onChangeArea: (id: string | undefined) => void;
}) {
  return (
    <section className="flex flex-wrap items-end justify-between gap-3 rounded-md border border-border bg-surface-overlay/50 p-3">
      <div className="flex flex-col gap-1.5">
        <BarraPeriodo ctrl={per} presets={PRESETS_PAROS} />
        {per.periodo.parcialLabel ? (
          <p className="text-[11px] text-fg-tertiary">{per.periodo.parcialLabel}</p>
        ) : null}
      </div>
      <div className="flex items-end gap-2">
        <div>
          <Label htmlFor="base-area">Área</Label>
          <Select
            id="base-area"
            value={areaId ?? ''}
            onChange={(e) => onChangeArea(e.target.value || undefined)}
            className="min-w-[160px]"
          >
            <option value="">Todas</option>
            {recursos.areas.map((a) => (
              <option key={a.id} value={a.id}>
                {a.nombre}
              </option>
            ))}
          </Select>
        </div>
        {areaId ? (
          <Button variant="outline" size="sm" onClick={() => onChangeArea(undefined)}>
            Quitar área
          </Button>
        ) : null}
      </div>
    </section>
  );
}

// =============================================================================
// RankingLineasTab — ex-C6 (ranking de pérdida por línea)
// =============================================================================

function RankingLineasTab({
  base,
  per,
  focusLineaId,
}: {
  base: BaseFiltros;
  per: ReturnType<typeof usePeriodo>;
  focusLineaId: string | null;
}) {
  // Control propio del tab: comparar. El ORDEN ya no se manda al API — la tabla
  // se ordena por columnas en el cliente (ver TablaDetalle). Granularidad fija en 'dia'.
  const [opciones, setOpciones] = useState<{ comparar?: boolean }>({});
  const [drillLinea, setDrillLinea] = useState<{
    id: string;
    nombre: string;
    unidades: number;
  } | null>(null);
  const filaFocusRef = useRef<HTMLTableRowElement | null>(null);

  const filtros: AnalisisLineasQuery = useMemo(
    () => ({
      ...base,
      granularidad: 'dia',
      // Orden fijo al API; el ordenamiento visible es client-side por columna.
      ordenar_por: 'horas_paro',
      comparar: opciones.comparar,
      // DN8: el período anterior lo resuelve el selector compartido "a la misma
      // altura" (mismo corte de la semana/mes anterior), no N días simétricos.
      ...(opciones.comparar
        ? { comparar_desde: per.periodo.desdeAnt, comparar_hasta: per.periodo.hastaAnt }
        : {}),
    }),
    [base, opciones, per.periodo.desdeAnt, per.periodo.hastaAnt],
  );
  const etiquetaDelta = etiquetaComparativo(per);

  const { data, isLoading, isError, error } = useCoordinadorAnalisisLineas(filtros);

  useEffect(() => {
    if (focusLineaId && filaFocusRef.current) {
      filaFocusRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [focusLineaId, data]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-12">
        <Spinner className="size-6" />
        <span className="text-sm text-fg-tertiary">Cargando ranking…</span>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <Card>
        <CardContent className="py-6 text-center text-sm text-crit">
          {error instanceof Error ? error.message : 'No pudimos cargar el ranking.'}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <RankingRibbon
        data={data}
        comparando={Boolean(opciones.comparar)}
        onToggleComparar={() => setOpciones((o) => ({ ...o, comparar: !o.comparar }))}
      />

      <RankingOpcionesBar data={data} />

      <KpiCardsRow data={data} etiquetaDelta={etiquetaDelta} />

      <TablaDetalle
        data={data}
        lineaIdFocus={focusLineaId}
        filaFocusRef={filaFocusRef}
        onDrillLinea={(id, nombre, unidades) => setDrillLinea({ id, nombre, unidades })}
      />

      <HorasParoPorDiaChart data={data} />

      {drillLinea ? (
        <ModalParosCausal
          lineaId={drillLinea.id}
          lineaNombre={drillLinea.nombre}
          unidadesSinProducir={drillLinea.unidades}
          desde={data.filtros.desde}
          hasta={data.filtros.hasta}
          onClose={() => setDrillLinea(null)}
        />
      ) : null}

      <footer className="text-center text-[11px] text-fg-tertiary">
        {data.totales.lineas_evaluadas} líneas evaluadas
        {data.comparativo
          ? ` · comparado con ${fmtFechaCorta(data.comparativo.desde)} → ${fmtFechaCorta(data.comparativo.hasta)}`
          : ''}
      </footer>
    </div>
  );
}

function fmtUltimaActualizacion(iso: string | null): string {
  if (!iso) return 'sin captura';
  const ts = new Date(iso);
  const minutos = Math.round((Date.now() - ts.getTime()) / 60_000);
  if (minutos < 1) return 'hace instantes';
  if (minutos < 60) return `hace ${minutos} min`;
  const horas = Math.round(minutos / 60);
  if (horas < 24) return `hace ${horas} h`;
  return `hace ${Math.round(horas / 24)} d`;
}

function RankingRibbon({
  data,
  comparando,
  onToggleComparar,
}: {
  data: NonNullable<ReturnType<typeof useCoordinadorAnalisisLineas>['data']>;
  comparando: boolean;
  onToggleComparar: () => void;
}) {
  const plantasLabel = data.scope.plantas_asignadas.map((p) => p.nombre).join(' · ');
  const dias =
    Math.round(
      (new Date(data.filtros.hasta + 'T00:00:00Z').getTime() -
        new Date(data.filtros.desde + 'T00:00:00Z').getTime()) /
        86_400_000,
    ) + 1;

  const exportar = async () => {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) {
        toast.error('Sesión expirada');
        return;
      }
      const params = new URLSearchParams();
      if (data.filtros.desde) params.set('desde', data.filtros.desde);
      if (data.filtros.hasta) params.set('hasta', data.filtros.hasta);
      if (data.filtros.cliente_id) params.set('cliente_id', data.filtros.cliente_id);
      if (data.filtros.planta_id) params.set('planta_id', data.filtros.planta_id);
      if (data.filtros.area_id) params.set('area_id', data.filtros.area_id);
      const qs = params.toString();
      const res = await fetch(
        `${API_URL}/api/coordinador/cumplimiento/export${qs ? `?${qs}` : ''}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) {
        toast.error(`Error ${res.status}: no se pudo exportar`);
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `analisis_lineas_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Excel descargado');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error');
    }
  };

  return (
    <div className="flex flex-wrap items-baseline justify-between gap-3">
      <p className="text-[11px] text-fg-tertiary">
        {plantasLabel || data.tenant.nombre} ·{' '}
        <span className="tabular-nums">{data.totales.lineas_evaluadas}</span> líneas · ranking por
        pérdida operativa · <span className="tabular-nums">{dias}</span> días · Última actualización{' '}
        {fmtUltimaActualizacion(data.ultima_actualizacion)}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" size="sm" onClick={onToggleComparar}>
          {comparando ? '✓ Comparando' : 'Comparar período'}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={exportar}
          title="Descarga el Excel de Cumplimiento con estas fechas y área. El ranking de paros no tiene export propio todavía."
        >
          ↓ Exportar cumplimiento (Excel)
        </Button>
      </div>
    </div>
  );
}

// =============================================================================
// RankingOpcionesBar — controles propios del tab líneas (granularidad/ordenar)
// =============================================================================

function RankingOpcionesBar({
  data,
}: {
  data: NonNullable<ReturnType<typeof useCoordinadorAnalisisLineas>['data']>;
}) {
  if (data.totales.lineas_significativas_count === 0) return null;
  // Es un dato de concentración (top 3 con paro), no una alarma: tinta neutra.
  return (
    <section className="flex items-center justify-end rounded-md border border-border bg-surface-overlay/40 px-3 py-2">
      <div className="flex items-center gap-1.5 text-[11px] text-fg-secondary">
        <span className="tabular-nums">{data.totales.lineas_significativas_count}</span>{' '}
        {data.totales.lineas_significativas_count === 1
          ? 'línea concentra la mayor pérdida'
          : 'líneas concentran la mayor pérdida'}{' '}
        (top 3 con paro)
      </div>
    </section>
  );
}

function FiltroBloque({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
        {label}
      </span>
      {children}
    </div>
  );
}

// =============================================================================
// KpiCardsRow (tab líneas)
// =============================================================================

// Regla ÚNICA que enciende el rojo en esta fila de KPIs (mockup validado):
// la línea top es crítica si perdió más de 1 h que afecta disponibilidad Y su
// cumplimiento vs capacidad está por debajo de 70 %. Sin regla, sin rojo.
const REGLA_LINEA_CRITICA = 'regla: >1 h que afecta + cumpl. <70 %';
function lineaTopEsCritica(top: { horas_paro: number; cumplimiento_pct: number | null }) {
  return top.horas_paro > 1 && top.cumplimiento_pct !== null && top.cumplimiento_pct < 70;
}

function KpiCardsRow({
  data,
  etiquetaDelta,
}: {
  data: NonNullable<ReturnType<typeof useCoordinadorAnalisisLineas>['data']>;
  etiquetaDelta: string;
}) {
  const { totales, serie_temporal, comparativo } = data;
  const sparkHoras = serie_temporal.map((s) => ({ fecha: s.periodo, valor: s.horas_paro }));
  const sparkUni = serie_temporal.map((s) => ({
    fecha: s.periodo,
    valor: s.unidades_sin_producir,
  }));
  const enCurso = totales.paros_en_curso ?? 0;
  const top = totales.linea_top_perdida;

  return (
    <section className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <KpiCard
        label="Paro que afecta disponibilidad"
        value={fmtHoras(totales.horas_paro_total)}
        tone="neutral"
        sparklinePuntos={sparkHoras}
        delta={comparativo?.delta_horas_paro ?? null}
        deltaLabel={
          comparativo
            ? `${fmtDeltaHoras(comparativo.delta_horas_paro) || '='} ${etiquetaDelta} (bajar es bueno)`
            : fmtEventos(totales.paros_count_total, 'cerrado')
        }
        deltaTone={comparativo && comparativo.delta_horas_paro < 0 ? 'ok' : 'neutral'}
        extra={
          enCurso > 0 ? (
            <span className="inline-flex items-center gap-1.5 text-[10.5px] text-fg-secondary">
              <span aria-hidden className="inline-block size-1.5 rounded-full bg-fg-tertiary" />
              {enCurso} en curso — aparte, no {enCurso === 1 ? 'suma' : 'suman'}
            </span>
          ) : comparativo ? (
            <span className="text-[10.5px] text-fg-tertiary">
              {fmtEventos(totales.paros_count_total, 'cerrado')}
            </span>
          ) : null
        }
      />
      <KpiCard
        label="Unidades sin producir"
        value={fmtNumero(totales.unidades_sin_producir_total)}
        valueSuffix=" paq"
        tone="neutral"
        sparklinePuntos={sparkUni}
        delta={comparativo?.delta_unidades_sin_producir ?? null}
        deltaLabel={
          comparativo
            ? `${comparativo.delta_unidades_sin_producir > 0 ? '+' : comparativo.delta_unidades_sin_producir < 0 ? '−' : ''}${fmtNumero(Math.abs(comparativo.delta_unidades_sin_producir))} ${etiquetaDelta} (bajar es bueno)`
            : 'estimado: tiempo de paro × capacidad'
        }
        deltaTone={comparativo && comparativo.delta_unidades_sin_producir < 0 ? 'ok' : 'neutral'}
        extra={
          comparativo ? (
            <span className="text-[10.5px] text-fg-tertiary">
              estimado: tiempo de paro × capacidad
            </span>
          ) : null
        }
      />
      <KpiCard
        label="Pérdida en pesos"
        value={totales.perdida_pesos_total != null ? fmtCop(totales.perdida_pesos_total) : '—'}
        tone="neutral"
        deltaLabel={
          totales.perdida_pesos_total != null
            ? 'valor de venta no producido por paros · precio vigente por SKU'
            : 'Sin datos de costos cargados'
        }
        deltaTone="neutral"
      />
      <KpiCard
        label="Línea con mayor pérdida"
        value={top?.linea_nombre ?? '—'}
        tone="neutral"
        deltaLabel={
          top
            ? `${fmtHoras(top.horas_paro)} · ${fmtNumero(top.unidades_sin_producir)} paq sin producir${
                top.cumplimiento_pct !== null ? ` · ${fmtPct(top.cumplimiento_pct)} cumpl.` : ''
              }`
            : 'sin paros cerrados en el período'
        }
        deltaTone="neutral"
        extra={
          top && lineaTopEsCritica(top) ? (
            <span className="inline-flex flex-wrap items-center gap-1.5">
              <Badge tone="crit">⚠ crítico</Badge>
              <span className="text-[10px] text-fg-tertiary">{REGLA_LINEA_CRITICA}</span>
            </span>
          ) : top ? (
            <span className="text-[10px] text-fg-tertiary">sin alarma · {REGLA_LINEA_CRITICA}</span>
          ) : null
        }
      />
    </section>
  );
}

function KpiCard({
  label,
  value,
  valueSuffix,
  tone,
  sparklinePuntos,
  delta,
  deltaLabel,
  deltaTone,
  extra,
}: {
  label: string;
  value: string;
  valueSuffix?: string;
  tone: 'ok' | 'warn' | 'crit' | 'neutral';
  sparklinePuntos?: Array<{ fecha: string; valor: number | null }>;
  delta?: number | null;
  deltaLabel: string;
  deltaTone: 'ok' | 'warn' | 'crit' | 'neutral';
  /** Línea adicional (chip "en curso", regla de la alarma, base del cálculo). */
  extra?: React.ReactNode;
}) {
  return (
    <Card>
      <CardContent className="flex flex-col gap-2 p-3">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
            {label}
          </span>
          {delta !== null && delta !== undefined ? (
            <span
              aria-hidden
              className={cn(
                'inline-block size-1.5 rounded-full',
                deltaTone === 'ok' ? 'bg-ok' : deltaTone === 'crit' ? 'bg-crit' : 'bg-fg-tertiary',
              )}
            />
          ) : null}
        </div>
        <div className="flex items-end justify-between gap-2">
          <span
            className={cn(
              'font-semibold tabular-nums tracking-tight text-[28px] leading-none',
              tone === 'ok'
                ? 'text-ok'
                : tone === 'crit'
                  ? 'text-crit'
                  : tone === 'warn'
                    ? 'text-warn'
                    : 'text-fg',
            )}
          >
            {value}
            {valueSuffix ? (
              <span className="text-[14px] text-fg-tertiary">{valueSuffix}</span>
            ) : null}
          </span>
          {sparklinePuntos && sparklinePuntos.length > 1 ? (
            <Sparkline puntos={sparklinePuntos} tone={tone} width={72} height={26} />
          ) : null}
        </div>
        <p className="text-[10.5px] text-fg-tertiary">{deltaLabel}</p>
        {extra ? <div className="leading-tight">{extra}</div> : null}
      </CardContent>
    </Card>
  );
}

// =============================================================================
// HorasParoPorDiaChart — BarChart vertical: horas de paro por día (serie_temporal)
// =============================================================================

function HorasParoPorDiaChart({
  data,
}: {
  data: NonNullable<ReturnType<typeof useCoordinadorAnalisisLineas>['data']>;
}) {
  const chartData = useMemo(
    () =>
      data.serie_temporal.map((s) => ({
        label: fmtFechaCorta(s.periodo),
        horas: Number(s.horas_paro.toFixed(2)),
        paros_count: s.paros_count,
      })),
    [data.serie_temporal],
  );

  return (
    <Card>
      <CardContent className="flex flex-col gap-3 p-3">
        <div>
          <h2 className="text-sm font-medium text-fg">Horas de paro por día</h2>
          <p className="text-[11px] text-fg-tertiary">
            Evolución diaria · misma medida, misma tinta — solo paros cerrados que afectan
            disponibilidad
          </p>
        </div>

        {chartData.length === 0 ? (
          <p className="py-6 text-center text-sm text-fg-tertiary">
            Sin datos en el rango seleccionado.
          </p>
        ) : (
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={chartData} margin={{ top: 16, right: 16, left: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
              <XAxis
                dataKey="label"
                stroke="var(--color-fg-tertiary)"
                tick={{ fontSize: 10.5, fill: 'var(--color-fg-tertiary)' }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                stroke="var(--color-fg-tertiary)"
                tick={{ fontSize: 10.5, fill: 'var(--color-fg-tertiary)' }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                cursor={{ fill: 'var(--color-surface-overlay)', fillOpacity: 0.4 }}
                content={<ParosBarTooltip />}
              />
              <Bar dataKey="horas" fill="var(--chart-1)" fillOpacity={0.9} radius={[3, 3, 0, 0]}>
                <LabelList
                  content={
                    <EtiquetaValor
                      formatter={(v) => fmtHoras(v)}
                      paso={pasoEtiquetas(chartData.length)}
                      total={chartData.length}
                    />
                  }
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}

// =============================================================================
// TablaDetalle (tab líneas)
// =============================================================================

type SortCol =
  | 'linea'
  | 'horas_paro'
  | 'paros_count'
  | 'unidades_sin_producir'
  | 'perdida_pesos'
  | 'cumplimiento_pct'
  | 'estado';
type SortState = { col: SortCol; dir: 'asc' | 'desc' };

/** Header de columna clickeable: alterna asc/desc, marca la columna activa. */
function SortHeader({
  label,
  col,
  sort,
  onSort,
  align = 'right',
}: {
  label: string;
  col: SortCol;
  sort: SortState;
  onSort: (col: SortCol) => void;
  align?: 'left' | 'right';
}) {
  const active = sort.col === col;
  return (
    <th className={cn('px-3 py-2', align === 'left' ? 'text-left' : 'text-right')}>
      <button
        type="button"
        onClick={() => onSort(col)}
        className={cn(
          'inline-flex items-center gap-1 hover:text-fg',
          align === 'right' && 'flex-row-reverse',
          active && 'text-fg',
        )}
      >
        <span aria-hidden className={cn('text-[8px]', active ? 'text-fg' : 'text-fg-tertiary/40')}>
          {active ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}
        </span>
        {label}
      </button>
    </th>
  );
}

function TablaDetalle({
  data,
  lineaIdFocus,
  filaFocusRef,
  onDrillLinea,
}: {
  data: NonNullable<ReturnType<typeof useCoordinadorAnalisisLineas>['data']>;
  lineaIdFocus: string | null;
  filaFocusRef: React.MutableRefObject<HTMLTableRowElement | null>;
  onDrillLinea: (id: string, nombre: string, unidades: number) => void;
}) {
  const [sort, setSort] = useState<SortState>({ col: 'horas_paro', dir: 'desc' });
  const onSort = (col: SortCol) =>
    setSort((s) =>
      s.col === col ? { col, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'desc' },
    );

  const lineasOrdenadas = useMemo(() => {
    const rank = { ok: 0, warn: 1, crit: 2 } as const;
    const f = sort.dir === 'asc' ? 1 : -1;
    return [...data.lineas].sort((a, b) => {
      switch (sort.col) {
        case 'linea':
          return f * a.linea_nombre.localeCompare(b.linea_nombre);
        case 'paros_count':
          return f * (a.paros_count - b.paros_count);
        case 'unidades_sin_producir':
          return f * (a.unidades_sin_producir - b.unidades_sin_producir);
        case 'perdida_pesos':
          return f * ((a.perdida_pesos ?? 0) - (b.perdida_pesos ?? 0));
        case 'cumplimiento_pct': {
          // null (línea sin capacidad/cumplimiento) siempre al final
          if (a.cumplimiento_pct === null && b.cumplimiento_pct === null) return 0;
          if (a.cumplimiento_pct === null) return 1;
          if (b.cumplimiento_pct === null) return -1;
          return f * (a.cumplimiento_pct - b.cumplimiento_pct);
        }
        case 'estado':
          return f * (rank[estadoLinea(a).tono] - rank[estadoLinea(b).tono]);
        default:
          return f * (a.horas_paro - b.horas_paro);
      }
    });
  }, [data.lineas, sort]);

  return (
    <Card>
      <CardContent className="flex flex-col gap-3 p-3">
        <div>
          <h2 className="text-sm font-medium text-fg">Desglose por línea</h2>
          <p className="text-[11px] text-fg-tertiary">
            <span className="tabular-nums">{data.lineas.length}</span> líneas · clic en una columna
            para ordenar · Estado: crítico si cumplimiento &lt;70 %; atención si &lt;90 % o ≥8 h de
            paro
          </p>
        </div>

        {data.lineas.length === 0 ? (
          <p className="py-4 text-center text-sm text-fg-tertiary">
            Sin datos en el rango seleccionado.
          </p>
        ) : (
          <div className="max-h-[70vh] overflow-auto rounded-md border border-border">
            <table className="min-w-full text-[12px]">
              <thead className="sticky top-0 z-10 bg-surface-overlay">
                <tr className="bg-surface-overlay text-[10px] uppercase tracking-wider text-fg-tertiary">
                  <SortHeader label="Línea" col="linea" sort={sort} onSort={onSort} align="left" />
                  <SortHeader label="Horas paro" col="horas_paro" sort={sort} onSort={onSort} />
                  <SortHeader label="Eventos" col="paros_count" sort={sort} onSort={onSort} />
                  <th className="px-3 py-2 text-left">Top causal</th>
                  <SortHeader
                    label="Uni. perdidas"
                    col="unidades_sin_producir"
                    sort={sort}
                    onSort={onSort}
                  />
                  <SortHeader label="Pérdida $" col="perdida_pesos" sort={sort} onSort={onSort} />
                  <SortHeader label="% Cumpl." col="cumplimiento_pct" sort={sort} onSort={onSort} />
                  <SortHeader label="Estado" col="estado" sort={sort} onSort={onSort} />
                  <th className="px-3 py-2 text-right"></th>
                </tr>
              </thead>
              <tbody>
                {lineasOrdenadas.map((l) => {
                  const isFocus = lineaIdFocus === l.linea_id;
                  const estado = estadoLinea(l);
                  return (
                    <tr
                      key={l.linea_id}
                      ref={isFocus ? filaFocusRef : null}
                      className={cn(
                        'border-b border-border last:border-b-0 hover:bg-surface-overlay/30',
                        isFocus && 'bg-surface-overlay/60 ring-1 ring-fg/40',
                      )}
                    >
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1.5">
                          <span
                            aria-hidden
                            className={cn(
                              'inline-block size-1.5 rounded-full',
                              estado.tono === 'ok'
                                ? 'bg-ok'
                                : estado.tono === 'warn'
                                  ? 'bg-warn'
                                  : 'bg-crit',
                            )}
                          />
                          <span className="font-medium text-fg">{l.linea_nombre}</span>
                        </div>
                        <div className="text-[10.5px] text-fg-tertiary">
                          {l.planta_nombre} · {l.area_nombre}
                        </div>
                      </td>
                      <td
                        className={cn(
                          'px-3 py-2 text-right tabular-nums',
                          l.horas_paro > 0 ? 'text-fg' : 'text-fg-tertiary',
                        )}
                      >
                        {l.horas_paro > 0 ? fmtHoras(l.horas_paro) : '—'}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-fg-secondary">
                        {l.paros_count}
                      </td>
                      <td className="px-3 py-2 text-fg-secondary">
                        {l.top_causal_nombre ?? <span className="text-fg-tertiary">—</span>}
                      </td>
                      <td
                        className={cn(
                          'px-3 py-2 text-right tabular-nums',
                          l.unidades_sin_producir > 0 ? 'text-fg' : 'text-fg-tertiary',
                        )}
                      >
                        {l.unidades_sin_producir > 0 ? fmtNumero(l.unidades_sin_producir) : '—'}
                      </td>
                      <td
                        className={cn(
                          'px-3 py-2 text-right tabular-nums',
                          l.perdida_pesos != null && l.perdida_pesos > 0
                            ? 'text-fg'
                            : 'text-fg-tertiary',
                        )}
                      >
                        {l.perdida_pesos != null && l.perdida_pesos > 0
                          ? fmtCop(l.perdida_pesos)
                          : '—'}
                      </td>
                      <td
                        className={cn(
                          'px-3 py-2 text-right tabular-nums font-medium',
                          toneTextClass(semaforoCumplimiento(l.cumplimiento_pct)),
                        )}
                      >
                        {fmtPct(l.cumplimiento_pct, 1)}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Badge tone={estado.tono}>{estado.label}</Badge>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          type="button"
                          onClick={() =>
                            onDrillLinea(
                              l.linea_id,
                              `${l.linea_codigo} ${l.linea_nombre}`,
                              l.unidades_sin_producir,
                            )
                          }
                          className="text-[11px] text-fg-secondary underline hover:text-fg"
                        >
                          Ver paros
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// =============================================================================
// ModalParosCausal — drill-down por causal (sin cambios funcionales)
// =============================================================================

function ModalParosCausal({
  lineaId,
  lineaNombre,
  unidadesSinProducir,
  desde,
  hasta,
  onClose,
}: {
  lineaId: string;
  lineaNombre: string;
  unidadesSinProducir: number;
  desde: string;
  hasta: string;
  onClose: () => void;
}) {
  // Misma base que la fila del ranking que abre este modal (B1): solo categorías
  // que afectan disponibilidad. Antes el modal sumaba TODOS los paros y no
  // coincidía con la fila.
  const { data, isLoading } = useCoordinadorAnalisisLineaParos(lineaId, desde, hasta, {
    soloAfectaDisponibilidad: true,
  });

  const topCausales = useMemo(() => {
    if (!data) return [];
    const sorted = [...data.causales].sort((a, b) => b.minutos_totales - a.minutos_totales);
    if (sorted.length <= 6) return sorted;
    const top5 = sorted.slice(0, 5);
    const restoMin = sorted.slice(5).reduce((s, c) => s + c.minutos_totales, 0);
    const restoPct = sorted.slice(5).reduce((s, c) => s + c.pct_minutos, 0);
    const restoParos = sorted.slice(5).reduce((s, c) => s + c.paros_count, 0);
    return [
      ...top5,
      {
        causal_id: 'otros',
        causal_madre_id: 'otros',
        causal_madre_nombre: 'Otros',
        causal_nombre: `Otros (${sorted.length - 5} causales)`,
        categoria_id: 'otros',
        categoria_nombre: 'Otros',
        categoria_color: null,
        paros_count: restoParos,
        minutos_totales: restoMin,
        pct_minutos: restoPct,
      },
    ];
  }, [data]);

  const promedioPorEvento =
    data && data.totales.paros_count > 0
      ? data.totales.minutos_totales / data.totales.paros_count
      : 0;

  type CausalRow = NonNullable<typeof data>['causales'][number];
  const causalesAgrupadas = useMemo(() => {
    const m = new Map<string, CausalRow[]>();
    if (!data) return m;
    const sorted = [...data.causales].sort((a, b) => b.minutos_totales - a.minutos_totales);
    for (const c of sorted) {
      const k = c.categoria_nombre || '—';
      const arr = m.get(k) ?? [];
      arr.push(c);
      m.set(k, arr);
    }
    return m;
  }, [data]);

  // Series categóricas (identidad de causal), no semáforo ni marca.
  const PALETA = [
    'var(--chart-1)',
    'var(--chart-2)',
    'var(--chart-3)',
    'var(--chart-4)',
    'var(--chart-5)',
    'var(--color-fg-tertiary)',
  ];

  return (
    <Dialog open onClose={onClose} className="max-w-6xl">
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-base">
            <span className="font-medium text-fg">{lineaNombre}</span>{' '}
            <span className="text-fg-tertiary">· paros que afectan disponibilidad, por causal</span>
          </DialogTitle>
          <p className="mt-0.5 text-[11px] text-fg-tertiary">
            Período {fmtFechaCorta(desde)} → {fmtFechaCorta(hasta)} ·{' '}
            <span className="tabular-nums">
              {Math.round(
                (new Date(hasta + 'T00:00:00Z').getTime() -
                  new Date(desde + 'T00:00:00Z').getTime()) /
                  86_400_000,
              ) + 1}
            </span>{' '}
            días
          </p>
        </DialogHeader>

        {isLoading || !data ? (
          <div className="flex items-center justify-center gap-2 py-12">
            <Spinner className="size-6" />
            <span className="text-sm text-fg-tertiary">Cargando paros…</span>
          </div>
        ) : data.causales.length === 0 ? (
          <div className="py-12 text-center">
            <p className="text-sm text-fg-secondary">
              🎉 Sin paros registrados en esta línea durante el período.
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
              <ModalStat
                label="Tiempo perdido"
                value={fmtHoras(data.totales.minutos_totales / 60)}
                sub={
                  data.totales.en_curso
                    ? `solo cerrados · ${data.totales.en_curso} en curso aparte`
                    : 'solo paros cerrados'
                }
                tone="neutral"
              />
              <ModalStat
                label="Unidades sin producir"
                value={unidadesSinProducir > 0 ? `${fmtNumero(unidadesSinProducir)} paq` : '—'}
                sub="estimado: tiempo de paro × capacidad"
                tone="neutral"
                emphasizeValue={false}
              />
              <ModalStat
                label="Eventos"
                value={String(data.totales.paros_count)}
                sub={`${data.causales.length} causal${data.causales.length === 1 ? '' : 'es'} distintas`}
                tone="neutral"
              />
              <ModalStat
                label="Top causal"
                value={topCausales[0]?.causal_nombre ?? '—'}
                sub={
                  topCausales[0]
                    ? `${topCausales[0].pct_minutos.toFixed(1)}% del tiempo perdido`
                    : ''
                }
                tone="neutral"
                emphasizeValue={false}
              />
              <ModalStat
                label="Promedio por evento"
                value={fmtHoras(promedioPorEvento / 60)}
                sub={`${Math.round(promedioPorEvento)} min/paro`}
                tone="neutral"
              />
            </div>

            <div className="rounded-md border border-border bg-surface-overlay/30 p-3">
              <h3 className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
                Paros por día (minutos)
              </h3>
              {data.serie_diaria.length === 0 ? (
                <p className="py-8 text-center text-sm text-fg-tertiary">Sin datos en el rango.</p>
              ) : (
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart
                    data={data.serie_diaria.map((s) => ({
                      label: fmtFechaCorta(s.fecha),
                      minutos: Math.round(s.minutos_totales),
                      paros_count: s.paros_count,
                    }))}
                    margin={{ top: 16, right: 16, left: 8, bottom: 8 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="var(--color-border)"
                      vertical={false}
                    />
                    <XAxis
                      dataKey="label"
                      stroke="var(--color-fg-tertiary)"
                      tick={{ fontSize: 10, fill: 'var(--color-fg-tertiary)' }}
                      axisLine={false}
                      tickLine={false}
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      stroke="var(--color-fg-tertiary)"
                      tick={{ fontSize: 10.5, fill: 'var(--color-fg-tertiary)' }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      cursor={{ fill: 'var(--color-surface-overlay)', fillOpacity: 0.4 }}
                      content={<ParosBarTooltip />}
                    />
                    <Bar
                      dataKey="minutos"
                      fill="var(--chart-1)"
                      fillOpacity={0.9}
                      radius={[3, 3, 0, 0]}
                    >
                      <LabelList
                        content={
                          <EtiquetaValor
                            formatter={(v) => fmtNumero(v)}
                            paso={pasoEtiquetas(data.serie_diaria.length)}
                            total={data.serie_diaria.length}
                            fontSize={9.5}
                          />
                        }
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-[260px_1fr]">
              <div className="rounded-md border border-border bg-surface-overlay/30 p-3">
                <h3 className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
                  Distribución por causal
                </h3>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={topCausales.map((c, i) => ({
                        name: c.causal_nombre,
                        value: c.minutos_totales,
                        fill: PALETA[i] ?? 'var(--color-fg-tertiary)',
                      }))}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={48}
                      outerRadius={80}
                      paddingAngle={1}
                      stroke="var(--color-surface-base)"
                      strokeWidth={2}
                    />
                    <Tooltip
                      contentStyle={{
                        background: 'var(--color-surface-elevated)',
                        border: '1px solid var(--color-border)',
                        borderRadius: 4,
                        fontSize: 11,
                        color: 'var(--color-fg)',
                      }}
                      formatter={
                        ((value: unknown, name: unknown) => [
                          fmtHoras(Number(value ?? 0) / 60),
                          String(name ?? ''),
                        ]) as never
                      }
                    />
                  </PieChart>
                </ResponsiveContainer>
                <ul className="mt-1 flex flex-col gap-0.5 text-[10.5px]">
                  {topCausales.slice(0, 6).map((c, i) => (
                    <li key={c.causal_id} className="flex items-center gap-1.5">
                      <span
                        aria-hidden
                        className="inline-block size-2 flex-shrink-0 rounded-sm"
                        style={{ backgroundColor: PALETA[i] ?? 'var(--color-fg-tertiary)' }}
                      />
                      <span className="truncate text-fg-secondary" title={c.causal_nombre}>
                        {c.causal_nombre}
                      </span>
                      <span className="ml-auto tabular-nums text-fg-tertiary">
                        {c.pct_minutos.toFixed(1)}%
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-md border border-border bg-surface-base">
                <div className="border-b border-border px-3 py-2">
                  <h3 className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
                    Detalle · {data.causales.length} causa
                    {data.causales.length === 1 ? '' : 's'} distintas (ordenado por tiempo perdido)
                  </h3>
                </div>
                <div className="max-h-[420px] overflow-auto">
                  <table className="w-full text-[12px]">
                    <thead className="sticky top-0 z-10 bg-surface-overlay">
                      <tr className="text-[10px] uppercase tracking-wider text-fg-tertiary">
                        <th className="px-3 py-2 text-left">Causal específica</th>
                        <th className="px-2 py-2 text-right"># Eventos</th>
                        <th className="px-2 py-2 text-right">Tiempo</th>
                        <th className="px-3 py-2 text-left">% del total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.from(causalesAgrupadas.entries()).map(([cat, causales]) => (
                        <FragmentGrupo key={cat} categoria={cat} causales={causales} />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        )}
        <DialogFooter>
          <Button onClick={onClose}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ModalStat({
  label,
  value,
  sub,
  tone,
  emphasizeValue = true,
}: {
  label: string;
  value: string;
  sub?: string;
  tone: 'ok' | 'warn' | 'crit' | 'neutral';
  emphasizeValue?: boolean;
}) {
  const toneCls =
    tone === 'ok'
      ? 'text-ok'
      : tone === 'crit'
        ? 'text-crit'
        : tone === 'warn'
          ? 'text-warn'
          : 'text-fg';
  return (
    <div className="flex flex-col gap-1 rounded-md border border-border bg-surface-base px-3 py-2.5">
      <span className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
        {label}
      </span>
      <span
        className={cn(
          'tabular-nums leading-tight',
          emphasizeValue
            ? cn('text-[22px] font-semibold', toneCls)
            : cn('text-[13px] font-medium', toneCls),
        )}
        title={value}
      >
        {emphasizeValue ? value : <span className="block truncate">{value}</span>}
      </span>
      {sub ? <span className="text-[10.5px] text-fg-tertiary">{sub}</span> : null}
    </div>
  );
}

function FragmentGrupo({
  categoria,
  causales,
}: {
  categoria: string;
  causales: Array<{
    causal_madre_nombre: string;
    causal_nombre: string;
    categoria_color?: string | null;
    paros_count: number;
    minutos_totales: number;
    pct_minutos: number;
  }>;
}) {
  const porMadre = useMemo(() => {
    const m = new Map<string, typeof causales>();
    for (const c of causales) {
      const arr = m.get(c.causal_madre_nombre) ?? [];
      arr.push(c);
      m.set(c.causal_madre_nombre, arr);
    }
    return m;
  }, [causales]);

  // Color de la categoría (todos los ítems del grupo comparten la misma categoría).
  const catColor = causales[0]?.categoria_color;

  return (
    <>
      <tr className="bg-surface-overlay/60">
        <td
          colSpan={4}
          className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-fg-secondary"
        >
          <span className="flex items-center gap-1.5">
            {catColor && (
              <span
                aria-hidden
                className="inline-block size-2 flex-shrink-0 rounded-sm"
                style={{ backgroundColor: catColor }}
              />
            )}
            {categoria && categoria !== '—' ? categoria : 'Sin categoría'}
          </span>
        </td>
      </tr>
      {Array.from(porMadre.entries()).flatMap(([madre, lista]) =>
        lista.map((c, i) => (
          <tr
            key={`${madre}-${c.causal_nombre}-${i}`}
            className="border-b border-border last:border-b-0 hover:bg-surface-overlay/30"
          >
            <td className="px-3 py-1.5">
              <div className="text-fg">{nombreNormalizado(c.causal_nombre)}</div>
              <div className="text-[10.5px] text-fg-tertiary">{nombreNormalizado(madre)}</div>
            </td>
            <td className="px-2 py-1.5 text-right tabular-nums text-fg-secondary">
              {c.paros_count}
            </td>
            <td className="px-2 py-1.5 text-right tabular-nums font-medium text-fg">
              {fmtHorasCompacto(c.minutos_totales)}
            </td>
            <td className="px-3 py-1.5">
              <div className="flex items-center gap-2">
                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-surface-overlay">
                  <div
                    className={cn(
                      'h-full transition-all',
                      c.pct_minutos >= 25
                        ? 'bg-crit'
                        : c.pct_minutos >= 10
                          ? 'bg-warn'
                          : 'bg-fg-tertiary',
                    )}
                    style={{ width: `${Math.min(100, c.pct_minutos)}%` }}
                  />
                </div>
                <span className="w-12 shrink-0 text-right text-[11px] tabular-nums text-fg-secondary">
                  {c.pct_minutos.toFixed(1)}%
                </span>
              </div>
            </td>
          </tr>
        )),
      )}
    </>
  );
}

/** Versión compacta para celdas: muestra "1h 23m" / "45m" / "12s" según escala. */
function fmtHorasCompacto(minutos: number): string {
  if (minutos < 1) return '<1m';
  if (minutos < 60) return `${Math.round(minutos)}m`;
  return fmtHoras(minutos / 60);
}

// =============================================================================
// DistribucionParosTab — ex-C8 (distribución por causal)
// =============================================================================

// Paleta de SERIES para charts (DN6, capa dataviz): identidad de la causal,
// nunca marca ni semáforo. Orden fijo; la 6.ª/7.ª caen a tinta neutra ("Otras").
const CHART_COLORS = [
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
  'var(--chart-4)',
  'var(--chart-5)',
  'var(--fg-secondary)',
  'var(--fg-tertiary)',
];

function DistribucionParosTab({
  data,
  lineaId,
  onChangeLinea,
}: {
  data: CoordinadorAnalisisParos;
  lineaId: string | undefined;
  onChangeLinea: (id: string | undefined) => void;
}) {
  const tendenciaPivot = useMemo(
    () => pivotTendencia(data.tendencia_temporal),
    [data.tendencia_temporal],
  );
  const causalesMadre = useMemo(
    () =>
      data.por_causal_madre.map((m, i) => ({
        ...m,
        // Usar el color del catálogo si existe; si no, caer al color del sistema.
        color: m.categoria_color ?? CHART_COLORS[i % CHART_COLORS.length] ?? 'var(--fg-secondary)',
      })),
    [data.por_causal_madre],
  );

  // Subtítulo: planta filtrada (resuelta en recursos) o "todas las plantas".
  const plantaLabel = useMemo(() => {
    if (!data.filtros.planta_id) return 'todas las plantas';
    const p = data.recursos.plantas.find((x) => x.id === data.filtros.planta_id);
    return p ? p.nombre : 'todas las plantas';
  }, [data.filtros.planta_id, data.recursos.plantas]);

  // Líneas disponibles para el filtro propio del tab, acotadas por planta/área base.
  const lineasFiltradas = useMemo(() => {
    let l = data.recursos.lineas;
    if (data.filtros.planta_id) l = l.filter((x) => x.planta_id === data.filtros.planta_id);
    if (data.filtros.area_id) l = l.filter((x) => x.area_id === data.filtros.area_id);
    return l;
  }, [data.recursos.lineas, data.filtros.planta_id, data.filtros.area_id]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <p className="text-[11px] text-fg-tertiary">
          {plantaLabel} · paro total registrado (incluye programados) · causal madre del momento de
          captura
        </p>
        <div className="flex flex-col gap-1">
          <Label htmlFor="causal-lin">Línea</Label>
          <Select
            id="causal-lin"
            value={lineaId ?? ''}
            onChange={(e) => onChangeLinea(e.target.value || undefined)}
            className="min-w-[200px]"
          >
            <option value="">Todas</option>
            {lineasFiltradas.map((l) => (
              <option key={l.id} value={l.id}>
                {l.codigo} {l.nombre}
              </option>
            ))}
          </Select>
        </div>
      </div>

      {/* Alerta de calidad: top "Otro" */}
      {data.alerta_otro.pct_otros > 10 ? (
        <Card variant="warn-left">
          <CardContent className="flex items-start gap-3 p-4 pt-4">
            <span className="mt-0.5 text-warn">
              <Icon name="alert" size={18} stroke={1.6} />
            </span>
            <div className="flex-1">
              <p className="text-sm font-medium text-fg">
                Alerta de calidad de dato — alto uso de causales "Otro"
              </p>
              <p className="mt-1 text-xs text-fg-secondary">
                <strong className="text-warn">{data.alerta_otro.pct_otros.toFixed(1)}%</strong> de
                los minutos de paro están bajo causales "Otro" (
                {Math.round(data.alerta_otro.minutos_otros)} min en{' '}
                {data.alerta_otro.causales.length} causal
                {data.alerta_otro.causales.length === 1 ? '' : 'es'}). Los líderes podrían estar
                evitando categorizar paros específicos — revisar capacitación o agregar causales
                faltantes a la jerarquía.
              </p>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {/* KPI Totales */}
      <section>
        <Card>
          <CardContent className="grid grid-cols-2 gap-4 p-5 pt-5 md:grid-cols-4">
            <KpiCardShared
              label="Paro total registrado"
              value={data.totales.horas_totales.toFixed(1)}
              valueSuffix="h"
              sub={
                data.totales.en_curso
                  ? `${Math.round(data.totales.minutos_totales)} min cerrados · ${data.totales.en_curso} en curso — aparte, no suman`
                  : `${Math.round(data.totales.minutos_totales)} min · solo cerrados`
              }
              subTone="neutral"
            />
            <KpiCardShared
              label="Eventos cerrados"
              value={data.totales.paros_count.toLocaleString('es-CO')}
              sub={`${data.totales.dias_con_paros} día(s) con registros`}
              subTone="neutral"
              divider
            />
            <KpiCardShared
              label="Causales madre"
              value={String(data.por_causal_madre.length)}
              sub="con paros en el rango"
              divider
            />
            <KpiCardShared
              label="% en 'Otro'"
              value={data.alerta_otro.pct_otros.toFixed(1)}
              valueSuffix="%"
              sub={`${data.alerta_otro.causales.length} causal(es) · regla: >10 % alerta de calidad de dato`}
              subTone={data.alerta_otro.pct_otros > 10 ? 'warn' : 'neutral'}
              divider
            />
          </CardContent>
        </Card>
      </section>

      {/* Charts */}
      {data.totales.minutos_totales > 0 ? (
        <section className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Card>
            <CardContent className="p-4 pt-4">
              <h3 className="mb-3 text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
                Distribución por causal madre · clasificación del momento de captura (DN2)
              </h3>
              <DistribucionPie data={causalesMadre} />
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 pt-4">
              <h3 className="mb-3 text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
                Tendencia temporal (minutos por día)
              </h3>
              <TendenciaLine pivot={tendenciaPivot} madres={causalesMadre} />
            </CardContent>
          </Card>
        </section>
      ) : null}

      {/* ¿Cuándo ocurren? — perfil por turno + por hora */}
      <CuandoOcurrenSection
        porTurno={data.por_turno}
        porHora={data.por_hora}
        produccionPorTurno={data.produccion_por_turno}
        produccionPorHora={data.produccion_por_hora}
      />

      {/* Tabla causales específicas */}
      <section>
        <h2 className="mb-3 text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
          Detalle por causal específica ({data.por_causal_especifica.length})
        </h2>
        {data.por_causal_especifica.length === 0 ? (
          <Card>
            <CardContent className="py-6 text-center text-sm text-fg-tertiary">
              Sin paros registrados en el rango.
            </CardContent>
          </Card>
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>Categoría</TH>
                <TH>Causal madre</TH>
                <TH>Causal específica</TH>
                <TH align="right"># Paros</TH>
                <TH align="right">Minutos</TH>
                <TH align="right">% del total</TH>
              </TR>
            </THead>
            <TBody>
              {data.por_causal_especifica.map((c) => (
                <TR key={c.causal_id}>
                  <TD className="text-xs text-fg-tertiary">{c.categoria_nombre || '—'}</TD>
                  <TD>{nombreNormalizado(c.causal_madre_nombre)}</TD>
                  <TD>
                    <span className="flex items-center gap-2">
                      {nombreNormalizado(c.causal_nombre)}
                      {c.es_otro ? (
                        <Badge tone="warn" className="text-[9px]">
                          Otro
                        </Badge>
                      ) : null}
                    </span>
                  </TD>
                  <TD align="right" numeric>
                    {c.paros_count}
                  </TD>
                  <TD align="right" numeric>
                    {Math.round(c.minutos_totales)}
                  </TD>
                  <TD align="right" numeric className="text-fg-secondary">
                    {c.pct_minutos.toFixed(1)}%
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </section>
    </div>
  );
}

// =============================================================================
// CuandoOcurrenSection — perfil por turno + perfil por hora (BarCharts verticales)
// =============================================================================

function CuandoOcurrenSection({
  porTurno,
  porHora,
  produccionPorTurno,
  produccionPorHora,
}: {
  porTurno: ParoPorTurno[];
  porHora: ParoPorHora[];
  produccionPorTurno: ProduccionPorTurno[];
  produccionPorHora: ProduccionPorHora[];
}) {
  const turnoData = useMemo(() => {
    const paros = new Map(porTurno.map((t) => [t.turno_numero, t]));
    const produccion = new Map(produccionPorTurno.map((t) => [t.turno_numero, t]));
    const numeros = Array.from(new Set([...paros.keys(), ...produccion.keys()])).sort(
      (a, b) => a - b,
    );
    return numeros.map((numero) => {
      const p = paros.get(numero);
      const c = produccion.get(numero);
      return {
        label: p?.turno_nombre || c?.turno_nombre || `T${numero}`,
        minutos: Math.round(p?.minutos_totales ?? 0),
        paros_count: p?.paros_count ?? 0,
        cumplimiento_pct: c?.cumplimiento_meta_pct ?? null,
      };
    });
  }, [porTurno, produccionPorTurno]);

  const horaData = useMemo(() => {
    const paros = new Map(porHora.map((h) => [h.hora, h]));
    const produccion = new Map(produccionPorHora.map((h) => [h.hora, h]));
    const horas = Array.from(new Set([...paros.keys(), ...produccion.keys()])).sort(
      (a, b) => a - b,
    );
    return horas.map((hora) => {
      const p = paros.get(hora);
      const c = produccion.get(hora);
      return {
        label: `${String(hora).padStart(2, '0')}:00`,
        minutos: Math.round(p?.minutos_totales ?? 0),
        paros_count: p?.paros_count ?? 0,
        cumplimiento_pct: c?.cumplimiento_meta_pct ?? null,
      };
    });
  }, [porHora, produccionPorHora]);

  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
        ¿Cuándo ocurren?
      </h2>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardContent className="p-4 pt-4">
            <h3 className="mb-3 text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
              Paros por turno (minutos)
            </h3>
            {turnoData.length === 0 ? (
              <p className="py-8 text-center text-sm text-fg-tertiary">Sin datos en el rango.</p>
            ) : (
              <>
                <div className="mb-2 flex items-center gap-3 text-[10.5px] text-fg-tertiary">
                  <span className="flex items-center gap-1.5">
                    <span
                      className="inline-block size-2.5 rounded-sm"
                      style={{ background: 'var(--chart-1)' }}
                    />{' '}
                    Minutos de paro
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span
                      className="inline-block h-0.5 w-4 rounded"
                      style={{ background: 'var(--chart-2)' }}
                    />{' '}
                    Cumplimiento vs. meta
                  </span>
                </div>
                <ResponsiveContainer width="100%" height={220}>
                  <ComposedChart
                    data={turnoData}
                    margin={{ top: 16, right: 16, left: 8, bottom: 8 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="var(--color-border)"
                      vertical={false}
                    />
                    <XAxis
                      dataKey="label"
                      stroke="var(--color-fg-tertiary)"
                      tick={{ fontSize: 11, fill: 'var(--color-fg-tertiary)' }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      yAxisId="minutos"
                      stroke="var(--color-fg-tertiary)"
                      tick={{ fontSize: 10.5, fill: 'var(--color-fg-tertiary)' }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      yAxisId="pct"
                      orientation="right"
                      domain={[0, 130]}
                      tickFormatter={(v) => `${v}%`}
                      tick={{ fontSize: 10.5, fill: 'var(--color-fg-tertiary)' }}
                      axisLine={false}
                      tickLine={false}
                      width={36}
                    />
                    <Tooltip
                      cursor={{ fill: 'var(--color-surface-overlay)', fillOpacity: 0.4 }}
                      content={<ParosBarTooltip />}
                    />
                    <Bar
                      yAxisId="minutos"
                      dataKey="minutos"
                      fill="var(--chart-1)"
                      fillOpacity={0.9}
                      radius={[3, 3, 0, 0]}
                    >
                      <LabelList
                        content={
                          <EtiquetaValor
                            formatter={(v) => fmtTiempoMin(v)}
                            total={turnoData.length}
                          />
                        }
                      />
                    </Bar>
                    <Line
                      yAxisId="pct"
                      type="monotone"
                      dataKey="cumplimiento_pct"
                      stroke="var(--chart-2)"
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      connectNulls
                    >
                      <LabelList
                        content={
                          <EtiquetaValor
                            formatter={(v) => `${v.toFixed(0)}%`}
                            total={turnoData.length}
                            dy={-10}
                          />
                        }
                      />
                    </Line>
                  </ComposedChart>
                </ResponsiveContainer>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 pt-4">
            <h3 className="mb-3 text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
              Perfil por hora (minutos)
            </h3>
            {horaData.length === 0 ? (
              <p className="py-8 text-center text-sm text-fg-tertiary">Sin datos en el rango.</p>
            ) : (
              <>
                <div className="mb-2 flex items-center gap-3 text-[10.5px] text-fg-tertiary">
                  <span className="flex items-center gap-1.5">
                    <span
                      className="inline-block size-2.5 rounded-sm"
                      style={{ background: 'var(--chart-1)' }}
                    />{' '}
                    Minutos de paro
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span
                      className="inline-block h-0.5 w-4 rounded"
                      style={{ background: 'var(--chart-2)' }}
                    />{' '}
                    Cumplimiento vs. meta
                  </span>
                </div>
                <ResponsiveContainer width="100%" height={220}>
                  <ComposedChart
                    data={horaData}
                    margin={{ top: 16, right: 16, left: 8, bottom: 8 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="var(--color-border)"
                      vertical={false}
                    />
                    <XAxis
                      dataKey="label"
                      stroke="var(--color-fg-tertiary)"
                      tick={{ fontSize: 10, fill: 'var(--color-fg-tertiary)' }}
                      axisLine={false}
                      tickLine={false}
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      yAxisId="minutos"
                      stroke="var(--color-fg-tertiary)"
                      tick={{ fontSize: 10.5, fill: 'var(--color-fg-tertiary)' }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      yAxisId="pct"
                      orientation="right"
                      domain={[0, 130]}
                      tickFormatter={(v) => `${v}%`}
                      tick={{ fontSize: 10.5, fill: 'var(--color-fg-tertiary)' }}
                      axisLine={false}
                      tickLine={false}
                      width={36}
                    />
                    <Tooltip
                      cursor={{ fill: 'var(--color-surface-overlay)', fillOpacity: 0.4 }}
                      content={<ParosBarTooltip />}
                    />
                    <Bar
                      yAxisId="minutos"
                      dataKey="minutos"
                      fill="var(--chart-1)"
                      fillOpacity={0.9}
                      radius={[3, 3, 0, 0]}
                    >
                      <LabelList
                        content={
                          <EtiquetaValor
                            formatter={(v) => fmtNumero(v)}
                            paso={pasoEtiquetas(horaData.length, 8)}
                            total={horaData.length}
                            fontSize={9.5}
                          />
                        }
                      />
                    </Bar>
                    <Line
                      yAxisId="pct"
                      type="monotone"
                      dataKey="cumplimiento_pct"
                      stroke="var(--chart-2)"
                      strokeWidth={2}
                      dot={{ r: 2 }}
                      connectNulls
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </section>
  );
}

// =============================================================================
// DistribucionPie — PieChart Recharts con causales madre
// =============================================================================

type CausalMadreConColor = {
  causal_madre_id: string;
  causal_madre_nombre: string;
  paros_count: number;
  minutos_totales: number;
  pct_minutos: number;
  color: string;
};

function DistribucionPie({ data }: { data: CausalMadreConColor[] }) {
  const chartData = data
    .filter((d) => d.minutos_totales > 0)
    .map((d) => ({
      name: d.causal_madre_nombre,
      value: Math.round(d.minutos_totales),
      pct: d.pct_minutos,
      color: d.color,
    }));

  if (chartData.length === 0) {
    return <p className="py-8 text-center text-sm text-fg-tertiary">Sin datos para el gráfico.</p>;
  }

  return (
    <ResponsiveContainer width="100%" height={280}>
      <PieChart>
        <Pie
          data={chartData}
          dataKey="value"
          nameKey="name"
          cx="50%"
          cy="50%"
          outerRadius={90}
          innerRadius={50}
          paddingAngle={2}
          label={({ cx, cy, midAngle, outerRadius, percent }) =>
            etiquetaDonutPct({
              cx,
              cy,
              midAngle,
              outerRadius,
              percent,
              fill: 'var(--fg-secondary)',
            })
          }
          labelLine={false}
        >
          {chartData.map((d, i) => (
            <Cell key={i} fill={d.color} stroke="var(--surface-elevated)" strokeWidth={2} />
          ))}
        </Pie>
        <Tooltip
          contentStyle={{
            background: 'var(--surface-elevated)',
            border: '1px solid var(--border)',
            borderRadius: 4,
            fontSize: 12,
            color: 'var(--fg)',
          }}
          formatter={
            ((value: unknown, name: unknown, props: { payload?: { pct?: number } }) => [
              `${typeof value === 'number' ? value.toLocaleString('es-CO') : value} min (${props.payload?.pct?.toFixed(1) ?? 0}%)`,
              String(name ?? ''),
            ]) as never
          }
        />
        <Legend wrapperStyle={{ fontSize: 11, color: 'var(--fg-secondary)' }} iconType="circle" />
      </PieChart>
    </ResponsiveContainer>
  );
}

// =============================================================================
// TendenciaLine — LineChart con una serie por causal madre
// =============================================================================

/** Pivotea tendencia: array de {fecha, causal_madre_id, minutos} → array de {fecha, [causal_madre_id]: minutos} */
function pivotTendencia(rows: TendenciaParo[]): Array<Record<string, string | number>> {
  const byFecha = new Map<string, Record<string, string | number>>();
  for (const r of rows) {
    let row = byFecha.get(r.fecha);
    if (!row) {
      row = { fecha: r.fecha };
      byFecha.set(r.fecha, row);
    }
    row[r.causal_madre_id] = r.minutos;
  }
  return Array.from(byFecha.values()).sort((a, b) =>
    String(a.fecha).localeCompare(String(b.fecha)),
  );
}

function TendenciaLine({
  pivot,
  madres,
}: {
  pivot: Array<Record<string, string | number>>;
  madres: CausalMadreConColor[];
}) {
  if (pivot.length === 0) {
    return <p className="py-8 text-center text-sm text-fg-tertiary">Sin datos para el gráfico.</p>;
  }

  return (
    <ResponsiveContainer width="100%" height={280}>
      <LineChart data={pivot} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
        <XAxis
          dataKey="fecha"
          stroke="var(--fg-tertiary)"
          tick={{ fontSize: 11, fill: 'var(--fg-tertiary)' }}
        />
        <YAxis
          stroke="var(--fg-tertiary)"
          tick={{ fontSize: 11, fill: 'var(--fg-tertiary)' }}
          label={{
            value: 'min',
            angle: -90,
            position: 'insideLeft',
            style: { fontSize: 10, fill: 'var(--fg-tertiary)' },
          }}
        />
        <Tooltip
          contentStyle={{
            background: 'var(--surface-elevated)',
            border: '1px solid var(--border)',
            borderRadius: 4,
            fontSize: 12,
            color: 'var(--fg)',
          }}
          labelStyle={{ color: 'var(--fg)' }}
          formatter={
            ((value: unknown, name: unknown) => {
              const madre = madres.find((m) => m.causal_madre_id === String(name));
              return [
                `${typeof value === 'number' ? Math.round(value).toLocaleString('es-CO') : value} min`,
                madre?.causal_madre_nombre ?? 'Causal',
              ];
            }) as never
          }
        />
        <Legend
          wrapperStyle={{ fontSize: 11, color: 'var(--fg-secondary)' }}
          iconType="line"
          formatter={(value: string) => {
            const madre = madres.find((m) => m.causal_madre_id === value);
            return madre?.causal_madre_nombre ?? 'Causal';
          }}
        />
        {madres.map((m) => (
          <Line
            key={m.causal_madre_id}
            type="monotone"
            dataKey={m.causal_madre_id}
            stroke={m.color}
            strokeWidth={2}
            dot={{ r: 3 }}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

// =============================================================================
// EntradaPitsTab — indicador "Entrada a Pits" (pedido Robotpack 2026-09)
//
// Promedio de minutos por EVENTO de paro para causales configurables (ej. FO01
// "Cambio de Rollo" meta 4 min, FO07 "Cambio de referencia" meta 10 min).
// Meta default por causal + override por línea (pantalla de configuración
// "Metas Entrada a Pits"). Gráficas de barras (líder / línea / supervisor) con
// línea de referencia roja de la meta, imitando el reporte del cliente.
// =============================================================================

type PitsFiltros = {
  linea_id?: string;
  lider_user_id?: string;
  turno_id?: string;
  supervisor_user_id?: string;
};

/** "María Fernanda Gómez" → "María G." (etiqueta corta para ejes). */
function nombreCortoPits(nombre: string, apellido: string): string {
  const n = nombre.trim().split(/\s+/)[0] ?? nombre;
  const a = apellido.trim().charAt(0);
  return a ? `${n} ${a}.` : n;
}

function fmtMinProm(n: number | null): string {
  if (n === null) return '—';
  return `${n.toLocaleString('es-CO', { maximumFractionDigits: 1 })} min`;
}

type PitsBarDatum = {
  /** Id de la dimensión (user_id o linea_id) — habilita el click-to-filter. */
  id: string;
  label: string;
  promedio: number;
  eventos: number;
  meta: number;
};

function PitsBarTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ payload?: PitsBarDatum }>;
}) {
  if (!active || !payload || payload.length === 0) return null;
  const d = payload[0]?.payload;
  if (!d) return null;
  return (
    <div className="rounded-md border border-border bg-surface-elevated px-2.5 py-1.5 text-xs text-fg shadow-sm">
      <p className="font-medium">{d.label}</p>
      <p className="mt-0.5 text-fg-secondary tabular-nums">
        {fmtMinProm(d.promedio)} promedio · {fmtEventos(d.eventos)}
      </p>
      <p className={cn('mt-0.5 tabular-nums', d.promedio <= d.meta ? 'text-ok' : 'text-crit')}>
        meta {fmtMinProm(d.meta)}
      </p>
    </div>
  );
}

/**
 * Gráfica de barras de promedio min/evento con línea de referencia de meta.
 * `onBarClick` habilita el drill-down: click en una barra → filtra todo el tab
 * por esa dimensión (líder / supervisor / línea).
 */
function PitsBarChart({
  titulo,
  data,
  meta,
  onBarClick,
}: {
  titulo: string;
  data: PitsBarDatum[];
  meta: number;
  onBarClick?: (id: string) => void;
}) {
  return (
    <div className="rounded-md border border-border bg-surface-overlay/30 p-3">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
          {titulo}
        </h3>
        <span className="text-[10.5px] tabular-nums text-crit">meta {fmtMinProm(meta)}</span>
      </div>
      {data.length === 0 ? (
        <p className="py-8 text-center text-sm text-fg-tertiary">Sin eventos en el rango.</p>
      ) : (
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={data} margin={{ top: 16, right: 16, left: 8, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
            <XAxis
              dataKey="label"
              stroke="var(--color-fg-tertiary)"
              tick={{ fontSize: 10, fill: 'var(--color-fg-tertiary)' }}
              axisLine={false}
              tickLine={false}
              interval={0}
              angle={data.length > 6 ? -30 : 0}
              textAnchor={data.length > 6 ? 'end' : 'middle'}
              height={data.length > 6 ? 46 : 30}
            />
            <YAxis
              stroke="var(--color-fg-tertiary)"
              tick={{ fontSize: 10.5, fill: 'var(--color-fg-tertiary)' }}
              axisLine={false}
              tickLine={false}
              // La meta siempre visible aunque todas las barras estén debajo.
              domain={[0, (dataMax: number) => Math.max(dataMax, meta * 1.25)]}
            />
            <Tooltip
              cursor={{ fill: 'var(--color-surface-overlay)', fillOpacity: 0.4 }}
              content={<PitsBarTooltip />}
            />
            <ReferenceLine
              y={meta}
              stroke="var(--color-crit)"
              strokeWidth={1.5}
              strokeDasharray="6 3"
              ifOverflow="extendDomain"
            />
            <Bar dataKey="promedio" fillOpacity={0.85} radius={[3, 3, 0, 0]}>
              {data.map((d) => (
                <Cell
                  key={d.id}
                  fill={d.promedio <= d.meta ? 'var(--color-ok)' : 'var(--color-crit)'}
                  className={onBarClick ? 'cursor-pointer' : undefined}
                  onClick={onBarClick ? () => onBarClick(d.id) : undefined}
                />
              ))}
              <LabelList
                content={
                  <EtiquetaValor
                    formatter={(v) => v.toLocaleString('es-CO', { maximumFractionDigits: 1 })}
                    paso={pasoEtiquetas(data.length)}
                    total={data.length}
                    fontSize={9.5}
                  />
                }
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

function EntradaPitsTab({ base }: { base: BaseFiltros }) {
  const [filtros, setFiltros] = useState<PitsFiltros>({});
  // Causal cuyo desglose muestran las gráficas (default: la primera).
  const [causalSel, setCausalSel] = useState<string | null>(null);

  const query: EntradaPitsQuery = useMemo(() => ({ ...base, ...filtros }), [base, filtros]);
  const { data, isLoading, isError, error } = useCoordinadorEntradaPits(query);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-16">
        <Spinner className="size-6" />
        <span className="text-sm text-fg-tertiary">Cargando Entrada a Pits…</span>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <Card>
        <CardContent className="py-10 text-center">
          <p className="text-sm font-medium text-crit">No pudimos cargar el indicador</p>
          <p className="mt-1 text-sm text-fg-secondary">
            {error instanceof Error ? error.message : 'Error desconocido'}
          </p>
        </CardContent>
      </Card>
    );
  }

  if (data.indicadores.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
          <p className="text-sm font-medium text-fg">
            Todavía no hay causales configuradas para Entrada a Pits
          </p>
          <p className="max-w-md text-sm text-fg-secondary">
            Defina qué causales de paro participan del indicador y su meta de minutos por evento
            (con overrides por línea si aplica) en la pantalla de configuración.
          </p>
          <Link
            to="/coordinador/configuracion/metas-pits"
            className="text-[12px] font-medium text-accent hover:underline"
          >
            Configurar metas →
          </Link>
        </CardContent>
      </Card>
    );
  }

  const causalActiva =
    data.indicadores.find((i) => i.causal_id === causalSel) ?? data.indicadores[0]!;

  const update = (patch: Partial<PitsFiltros>) => setFiltros((f) => ({ ...f, ...patch }));

  // Los dropdowns encadenan con el área de la barra base.
  const lineasOpciones = base.area_id
    ? data.recursos.lineas.filter((l) => l.area_id === base.area_id)
    : data.recursos.lineas;

  const personaDatum = (p: PitsPorPersona): PitsBarDatum => ({
    id: p.user_id,
    label: nombreCortoPits(p.nombre, p.apellido),
    promedio: p.promedio_minutos,
    eventos: p.eventos_count,
    meta: causalActiva.meta_minutos_default,
  });

  const porLider = data.por_lider.filter((p) => p.causal_id === causalActiva.causal_id);
  const porSupervisor = data.por_supervisor.filter((p) => p.causal_id === causalActiva.causal_id);
  const porLinea = data.por_linea.filter((p) => p.causal_id === causalActiva.causal_id);
  const tendencia = data.tendencia.filter((t) => t.causal_id === causalActiva.causal_id);

  const lineaDatum: PitsBarDatum[] = data.por_linea
    .filter((p) => p.causal_id === causalActiva.causal_id)
    .map((p) => ({
      id: p.linea_id,
      label: p.linea_codigo,
      promedio: p.promedio_minutos,
      eventos: p.eventos_count,
      meta: p.meta_minutos,
    }));

  // Historial: eventos de la causal activa (filtrado CLIENT-SIDE — el server
  // manda todas las causales participantes, capado a los 200 más recientes).
  const eventosCausal = data.eventos.filter((e) => e.causal_id === causalActiva.causal_id);
  const historialCapado = data.eventos.length < data.eventos_total;

  // Chips del drill activo: mismo estado que los dropdowns, con quitar rápido.
  const nombrePersona = (
    userId: string,
    recursos: Array<{ user_id: string; nombre: string; apellido: string }>,
    desglose: PitsPorPersona[],
  ): string => {
    const r = recursos.find((x) => x.user_id === userId);
    if (r) return `${r.nombre} ${r.apellido}`;
    const d = desglose.find((x) => x.user_id === userId);
    return d ? `${d.nombre} ${d.apellido}` : '—';
  };
  const chips: Array<{ key: keyof PitsFiltros; etiqueta: string; valor: string }> = [];
  if (filtros.linea_id) {
    const l = data.recursos.lineas.find((x) => x.id === filtros.linea_id);
    chips.push({
      key: 'linea_id',
      etiqueta: 'Línea',
      valor: l ? `${l.codigo} · ${l.nombre}` : '—',
    });
  }
  if (filtros.lider_user_id) {
    chips.push({
      key: 'lider_user_id',
      etiqueta: 'Líder',
      valor: nombrePersona(filtros.lider_user_id, data.recursos.lideres, data.por_lider),
    });
  }
  if (filtros.turno_id) {
    const t = data.recursos.turnos.find((x) => x.id === filtros.turno_id);
    chips.push({ key: 'turno_id', etiqueta: 'Turno', valor: t ? t.nombre : '—' });
  }
  if (filtros.supervisor_user_id) {
    chips.push({
      key: 'supervisor_user_id',
      etiqueta: 'Supervisor',
      valor: nombrePersona(
        filtros.supervisor_user_id,
        data.recursos.supervisores,
        data.por_supervisor,
      ),
    });
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Filtros adicionales del tab (línea / líder / turno / supervisor) */}
      <section className="rounded-md border border-border bg-surface-overlay/50 p-3">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <Label htmlFor="pits-linea">Línea</Label>
            <Select
              id="pits-linea"
              value={filtros.linea_id ?? ''}
              onChange={(e) => update({ linea_id: e.target.value || undefined })}
            >
              <option value="">Todas</option>
              {lineasOpciones.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.codigo} · {l.nombre}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <Label htmlFor="pits-lider">Líder</Label>
            <Select
              id="pits-lider"
              value={filtros.lider_user_id ?? ''}
              onChange={(e) => update({ lider_user_id: e.target.value || undefined })}
            >
              <option value="">Todos</option>
              {data.recursos.lideres.map((l) => (
                <option key={l.user_id} value={l.user_id}>
                  {l.nombre} {l.apellido}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <Label htmlFor="pits-turno">Turno</Label>
            <Select
              id="pits-turno"
              value={filtros.turno_id ?? ''}
              onChange={(e) => update({ turno_id: e.target.value || undefined })}
            >
              <option value="">Todos</option>
              {data.recursos.turnos.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.nombre}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <Label htmlFor="pits-supervisor">Supervisor</Label>
            <Select
              id="pits-supervisor"
              value={filtros.supervisor_user_id ?? ''}
              onChange={(e) => update({ supervisor_user_id: e.target.value || undefined })}
            >
              <option value="">Todos</option>
              {data.recursos.supervisores.map((s) => (
                <option key={s.user_id} value={s.user_id}>
                  {s.nombre} {s.apellido}
                </option>
              ))}
            </Select>
          </div>
        </div>
      </section>

      {/* Drill activo: chips con quitar rápido (mismo estado que los
          dropdowns; el click en una barra setea el mismo filtro) */}
      {chips.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[11px] text-fg-tertiary">Filtrando:</span>
          {chips.map((c) => (
            <span
              key={c.key}
              className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface-overlay px-2.5 py-1 text-[11px] text-fg"
            >
              <span className="text-fg-tertiary">{c.etiqueta}:</span>
              <span className="font-medium">{c.valor}</span>
              <button
                type="button"
                aria-label={`Quitar filtro de ${c.etiqueta.toLowerCase()}`}
                title={`Quitar filtro de ${c.etiqueta.toLowerCase()}`}
                className="rounded-full p-0.5 text-fg-tertiary hover:bg-surface-elevated hover:text-fg"
                onClick={() => update({ [c.key]: undefined })}
              >
                <Icon name="x" className="size-3" />
              </button>
            </span>
          ))}
          <Button variant="ghost" size="sm" onClick={() => setFiltros({})}>
            Quitar todos
          </Button>
        </div>
      )}

      {/* KPI por causal participante: promedio vs meta */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {data.indicadores.map((i) => {
          const tono =
            i.cumple_meta === null ? 'text-fg-secondary' : i.cumple_meta ? 'text-ok' : 'text-crit';
          return (
            <button
              key={i.causal_id}
              type="button"
              onClick={() => setCausalSel(i.causal_id)}
              className={cn(
                'rounded-md border bg-surface-overlay/30 p-3 text-left transition-colors',
                i.causal_id === causalActiva.causal_id
                  ? 'border-fg'
                  : 'border-border hover:border-fg-tertiary',
              )}
            >
              <p className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
                {nombreNormalizado(i.causal_nombre)}
              </p>
              <p className={cn('mt-1 text-2xl font-semibold tabular-nums', tono)}>
                {fmtMinProm(i.promedio_minutos)}
              </p>
              <p className="mt-0.5 text-[11px] text-fg-secondary tabular-nums">
                meta {fmtMinProm(i.meta_minutos_default)} · {fmtEventos(i.eventos_count)}
                {i.cumple_meta === null
                  ? ' · sin eventos en el rango'
                  : i.cumple_meta
                    ? ' · cumple'
                    : ' · no cumple'}
              </p>
            </button>
          );
        })}
      </div>

      {/* Selector de causal para el desglose (redundante con el click en la
          card, pero explícito en pantallas angostas) */}
      {data.indicadores.length > 1 && (
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-fg-tertiary">Desglose de:</span>
          <SegmentedControl<string>
            value={causalActiva.causal_id}
            onChange={(v) => setCausalSel(v)}
            size="sm"
            options={data.indicadores.map((i) => ({
              value: i.causal_id,
              label: nombreNormalizado(i.causal_nombre),
            }))}
          />
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <PitsBarChart
          titulo={`Promedio por líder · ${nombreNormalizado(causalActiva.causal_nombre)}`}
          data={porLider.map(personaDatum)}
          meta={causalActiva.meta_minutos_default}
          onBarClick={(id) => update({ lider_user_id: id })}
        />
        <PitsBarChart
          titulo={`Promedio por supervisor · ${nombreNormalizado(causalActiva.causal_nombre)}`}
          data={porSupervisor.map(personaDatum)}
          meta={causalActiva.meta_minutos_default}
          onBarClick={(id) => update({ supervisor_user_id: id })}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <PitsBarChart
          titulo={`Promedio por línea · ${nombreNormalizado(causalActiva.causal_nombre)}`}
          data={lineaDatum}
          meta={causalActiva.meta_minutos_default}
          onBarClick={(id) => update({ linea_id: id })}
        />

        {/* Tendencia diaria del promedio */}
        <div className="rounded-md border border-border bg-surface-overlay/30 p-3">
          <h3 className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
            Tendencia diaria · {nombreNormalizado(causalActiva.causal_nombre)}
          </h3>
          {tendencia.length === 0 ? (
            <p className="py-8 text-center text-sm text-fg-tertiary">Sin eventos en el rango.</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart
                data={tendencia.map((t) => ({
                  label: fmtFechaCorta(t.fecha),
                  promedio: t.promedio_minutos,
                  eventos: t.eventos_count,
                  meta: causalActiva.meta_minutos_default,
                }))}
                margin={{ top: 16, right: 16, left: 8, bottom: 8 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="var(--color-border)"
                  vertical={false}
                />
                <XAxis
                  dataKey="label"
                  stroke="var(--color-fg-tertiary)"
                  tick={{ fontSize: 10, fill: 'var(--color-fg-tertiary)' }}
                  axisLine={false}
                  tickLine={false}
                  interval="preserveStartEnd"
                />
                <YAxis
                  stroke="var(--color-fg-tertiary)"
                  tick={{ fontSize: 10.5, fill: 'var(--color-fg-tertiary)' }}
                  axisLine={false}
                  tickLine={false}
                  domain={[
                    0,
                    (dataMax: number) =>
                      Math.max(dataMax, causalActiva.meta_minutos_default * 1.25),
                  ]}
                />
                <Tooltip cursor={{ stroke: 'var(--color-border)' }} content={<PitsBarTooltip />} />
                <ReferenceLine
                  y={causalActiva.meta_minutos_default}
                  stroke="var(--color-crit)"
                  strokeWidth={1.5}
                  strokeDasharray="6 3"
                  ifOverflow="extendDomain"
                />
                <Line
                  type="monotone"
                  dataKey="promedio"
                  stroke="var(--color-info)"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Tabla por línea con meta RESUELTA (override o default) */}
      <Card>
        <CardContent className="p-0">
          <div className="border-b border-border px-3 py-2">
            <h3 className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
              Detalle por línea · {nombreNormalizado(causalActiva.causal_nombre)} · meta resuelta
              (override de línea o default)
            </h3>
          </div>
          {porLinea.length === 0 ? (
            <p className="py-8 text-center text-sm text-fg-tertiary">Sin eventos en el rango.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <THead>
                  <TR>
                    <TH>Línea</TH>
                    <TH className="text-right"># Eventos</TH>
                    <TH className="text-right">Minutos</TH>
                    <TH className="text-right">Promedio</TH>
                    <TH className="text-right">Meta</TH>
                    <TH>Estado</TH>
                  </TR>
                </THead>
                <TBody>
                  {porLinea.map((p) => (
                    <TR
                      key={`${p.causal_id}-${p.linea_id}`}
                      className="cursor-pointer hover:bg-surface-overlay/50"
                      title="Filtrar el tab por esta línea"
                      onClick={() => update({ linea_id: p.linea_id })}
                    >
                      <TD>
                        <span className="font-medium text-fg">{p.linea_codigo}</span>{' '}
                        <span className="text-fg-tertiary">{p.linea_nombre}</span>
                      </TD>
                      <TD className="text-right tabular-nums">{fmtNumero(p.eventos_count)}</TD>
                      <TD className="text-right tabular-nums">{fmtTiempoMin(p.minutos_totales)}</TD>
                      <TD
                        className={cn(
                          'text-right font-medium tabular-nums',
                          p.cumple_meta ? 'text-ok' : 'text-crit',
                        )}
                      >
                        {fmtMinProm(p.promedio_minutos)}
                      </TD>
                      <TD className="text-right tabular-nums">{fmtMinProm(p.meta_minutos)}</TD>
                      <TD>
                        <Badge tone={p.cumple_meta ? 'ok' : 'crit'}>
                          {p.cumple_meta ? 'Cumple' : 'No cumple'}
                        </Badge>
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Historial de eventos (validación de datos): cada fila = un evento de
          paro real. Filtrado client-side por la causal activa del desglose. */}
      <Card>
        <CardContent className="p-0">
          <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-border px-3 py-2">
            <h3 className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
              Historial de eventos · {nombreNormalizado(causalActiva.causal_nombre)}
            </h3>
            <span className="text-[10.5px] tabular-nums text-fg-tertiary">
              {fmtEventos(eventosCausal.length)}
              {historialCapado &&
                ` · mostrando los ${fmtNumero(data.eventos.length)} más recientes de ${fmtNumero(
                  data.eventos_total,
                )} en el rango (todas las causales)`}
            </span>
          </div>
          {eventosCausal.length === 0 ? (
            <p className="py-8 text-center text-sm text-fg-tertiary">
              Sin eventos de esta causal con los filtros actuales.
            </p>
          ) : (
            <div className="max-h-[480px] overflow-auto">
              <Table>
                <THead sticky>
                  <TR>
                    <TH>Fecha</TH>
                    <TH>Hora</TH>
                    <TH className="text-right">Minutos</TH>
                    <TH>Causal</TH>
                    <TH>Línea</TH>
                    <TH>Líder</TH>
                    <TH>Turno</TH>
                    <TH>Supervisor</TH>
                    <TH>Observaciones</TH>
                  </TR>
                </THead>
                <TBody>
                  {eventosCausal.map((e) => (
                    <TR key={e.paro_evento_id}>
                      <TD className="whitespace-nowrap tabular-nums">{fmtFechaCorta(e.fecha)}</TD>
                      <TD className="whitespace-nowrap tabular-nums">
                        {e.hora_inicio}–{e.hora_fin}
                      </TD>
                      <TD className="text-right font-medium tabular-nums">
                        {fmtMinProm(e.minutos)}
                      </TD>
                      <TD>{nombreNormalizado(e.causal_nombre)}</TD>
                      <TD>
                        <span className="font-medium text-fg">{e.linea_codigo}</span>{' '}
                        <span className="text-fg-tertiary">{e.linea_nombre}</span>
                      </TD>
                      <TD>{e.lider_nombre ?? '—'}</TD>
                      <TD className="whitespace-nowrap">{e.turno_nombre}</TD>
                      <TD>{e.supervisor_nombre ?? '—'}</TD>
                      <TD>
                        {e.observaciones ? (
                          <span
                            className="block max-w-[240px] truncate text-fg-secondary"
                            title={e.observaciones}
                          >
                            {e.observaciones}
                          </span>
                        ) : (
                          '—'
                        )}
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
